/** V9: start recommendation discovery on connection, not after scrolling.
 * Retains Shopify's rendered cards, quick-add and section-editor integration.
 */
class ProductRecommendations extends HTMLElement {
  static observedAttributes = ['data-product-id', 'data-section-id', 'data-intent', 'data-url'];
  static cache = new Map();
  #controller;
  #requestKey = '';
  #loadedKey = '';
  #generation = 0;
  #scheduled = false;
  #retryTimer;
  #retries = 0;
  connectedCallback() { this.#schedule(); }
  disconnectedCallback() {
    this.#generation++;
    this.#controller?.abort();
    this.#controller = undefined;
    this.#requestKey = '';
    clearTimeout(this.#retryTimer);
  }
  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue !== newValue && this.isConnected) {
      this.#retries = 0;
      this.#schedule();
    }
  }
  #schedule() {
    if (this.#scheduled) return;
    this.#scheduled = true;
    queueMicrotask(() => {
      this.#scheduled = false;
      if (this.isConnected) this.#load();
    });
  }
  async #load() {
    const { productId, sectionId, intent, url: endpoint } = this.dataset;
    if (!productId || !sectionId || !endpoint || !this.id) return;
    let url;
    try {
      url = new URL(endpoint, location.origin);
      if (url.origin !== location.origin) return;
      url.searchParams.set('product_id', productId);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('intent', intent || 'related');
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
    } catch { return; }
    const key = url.href;
    if (key === this.#loadedKey || key === this.#requestKey) return;
    if (!this.#loadedKey && this.dataset.recommendationsPerformed === 'true') {
      this.#loadedKey = key;
      return;
    }
    this.#controller?.abort();
    clearTimeout(this.#retryTimer);
    const controller = new AbortController();
    this.#controller = controller;
    this.#requestKey = key;
    const generation = ++this.#generation;
    this.setAttribute('aria-busy', 'true');
    this.classList.remove('hidden');
    delete this.dataset.error;
    let timedOut = false;
    const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 15000);
    try {
      const cached = ProductRecommendations.cache.get(key);
      let text = cached && Date.now() - cached.time < 300000 ? cached.html : null;
      if (text === null) {
        const response = await fetch(key, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        text = await response.text();
        ProductRecommendations.cache.set(key, { html: text, time: Date.now() });
        if (ProductRecommendations.cache.size > 12) {
          ProductRecommendations.cache.delete(ProductRecommendations.cache.keys().next().value);
        }
      }
      if (!this.isConnected || generation !== this.#generation) return;
      const documentFragment = new DOMParser().parseFromString(text, 'text/html');
      const result = documentFragment.getElementById(this.id);
      if (!result || result.tagName.toLowerCase() !== 'product-recommendations') {
        throw new Error('Recommendations section missing');
      }
      if (result.dataset.recommendationsPerformed !== 'true') {
        throw new Error('Recommendations response not ready');
      }
      const cards = result.querySelectorAll('product-card');
      const hasProducts = cards.length > 0 && !result.querySelector('[data-has-recommendations="false"]');
      if (!hasProducts) {
        this.replaceChildren();
        this.classList.add('hidden');
      } else {
        const rect = this.getBoundingClientRect();
        const nearViewport = rect.top < window.innerHeight + 800 && rect.bottom > -200;
        if (nearViewport) {
          // Horizon may render separate desktop and mobile lists. Prioritize
          // only the active list, never the hidden desktop copies on a phone.
          const hiddenBranch = matchMedia('(max-width: 749px)').matches ? '.hidden--mobile' : '.hidden--desktop';
          [...result.querySelectorAll('.card-gallery img')]
            .filter(image => !image.closest(hiddenBranch))
            .slice(0, 4)
            .forEach(image => {
              image.loading = 'eager';
              image.setAttribute('fetchpriority', 'auto');
            });
        }
        this.replaceChildren(...Array.from(result.childNodes));
      }
      this.dataset.recommendationsPerformed = 'true';
      this.#loadedKey = key;
      this.#retries = 0;
    } catch (error) {
      // Aborting an obsolete/disconnected request is not a storefront failure.
      if (!this.isConnected || generation !== this.#generation) return;
      if (error.name === 'AbortError' && !timedOut) return;
      ProductRecommendations.cache.delete(key);
      if (this.#retries < 1) {
        this.#retries++;
        this.#retryTimer = setTimeout(() => this.#schedule(), 1000);
      } else {
        // Never leave an endless empty skeleton after a real network failure.
        if (!this.#loadedKey) this.classList.add('hidden');
        this.dataset.error = 'Recommendations temporarily unavailable';
        console.warn('[Monkey Monkey] Recommendations:', error.message);
      }
    } finally {
      clearTimeout(timeout);
      if (generation === this.#generation) {
        this.#requestKey = '';
        this.#controller = undefined;
        this.setAttribute('aria-busy', 'false');
      }
    }
  }
}
if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
